import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import type { NavigationGroup } from 'src/app/shared/navigation/navigation-group.interface';
import type { NavigationItem } from 'src/app/shared/navigation/navigation-item.interface';
import { URL_PATH_SUMMARY } from 'src/app/utils/global-constants';
import type { EnrichedComponentCategory } from 'src/app/utils/models/enriched-component-category.type';

import { translate } from '../../shared/pipes/translate/translate.pipe';

export const NAVIGATION_ITEM_PREFIX = `OWCC-NAV-ITEM_`;

const NAVIGATION_ITEM_ID_PRECONFIG_SUFFIX = 'PRECONFIG';
const NAVIGATION_ITEM_ID_LENGTH_SUFFIX = 'LENGTH';

export function getCustomNavigationGroups(
  categoriesCollection: EnrichedComponentCategory[],
  displayPreconfigPage: boolean,
  displayVehicleLengthPage: boolean
): NavigationGroup[] {
  const createNavigationGroup = (
    displayPage: boolean,
    idSuffix: string,
    routePath: AppRoutePath,
    name: string
  ): NavigationGroup[] =>
    displayPage
      ? [
          {
            id: `${NAVIGATION_ITEM_PREFIX}_VEHICLE`,
            children: [
              {
                id: `${NAVIGATION_ITEM_PREFIX}_${idSuffix}`,
                routePath,
                name,
                isTranslated: true,
                parentName: 'navigationLabels.performance'
              }
            ]
          } as NavigationGroup
        ]
      : [];

  return [
    ...createNavigationGroup(
      displayPreconfigPage,
      NAVIGATION_ITEM_ID_PRECONFIG_SUFFIX,
      AppRoutePath.Preconfig,
      'navigationLabels.preconfig'
    ),
    ...createNavigationGroup(
      displayVehicleLengthPage,
      NAVIGATION_ITEM_ID_LENGTH_SUFFIX,
      AppRoutePath.VehicleLength,
      'navigationLabels.vehicleLength'
    ),
    ...getNavigationGroups(categoriesCollection)
  ];
}

function getNavigationGroups(categoriesCollection: EnrichedComponentCategory[]): NavigationGroup[] {
  const navigationGroups: NavigationGroup[] = [];

  // manually add motorization, because it's not delivered by backend
  const motorizationGroup: NavigationGroup = {
    id: `${NAVIGATION_ITEM_PREFIX}_PERFORMANCE`,
    children: [
      {
        id: `${NAVIGATION_ITEM_PREFIX}_MOTORIZATION`,
        routePath: AppRoutePath.Motorization,
        name: 'navigationLabels.motorization',
        translatedName: translate('navigationLabels.motorization'),
        isTranslated: true,
        parentName: 'navigationLabels.performance'
      }
    ]
  };
  navigationGroups.push(motorizationGroup);

  // add given navigationGroups
  for (const group of Object.values(categoriesCollection || [])) {
    const navigationGroup: NavigationGroup = {
      id: group.id,
      children: []
    };
    if (group.subcategories) {
      navigationGroup.name = group.name;
      for (const navigationItem of group.subcategories) {
        navigationGroup.children.push(getConfigurationStepNavigationItem(navigationItem));
      }
    } else {
      navigationGroup.children.push(getConfigurationStepNavigationItem(group));
    }
    navigationGroups.push(navigationGroup);
  }

  // manually add summary
  const summaryGroup: NavigationGroup = {
    id: `${NAVIGATION_ITEM_PREFIX}_SUMMARYGROUP`,
    children: [
      {
        id: `${NAVIGATION_ITEM_PREFIX}_SUMMARY`,
        routePath: AppRoutePath.Configuration,
        name: 'navigationLabels.summary',
        isTranslated: true,
        pathParams: {
          configurationStep: URL_PATH_SUMMARY.toLowerCase()
        }
      }
    ]
  };
  navigationGroups.push(summaryGroup);

  return navigationGroups;
}

function getConfigurationStepNavigationItem(category: EnrichedComponentCategory): NavigationItem {
  const configurationStep = category.id.toLowerCase();

  return {
    id: category.id,
    name: category.name || '', // there should be a name, thus no optional name property but a (questionable) fallback value
    routePath: AppRoutePath.Configuration,
    pathParams: { configurationStep },
    categoryViewType: category.categoryViewType
  };
}
